import * as React from 'react'
import styled from 'styled-components'
import ArticleItem from '../../components/Messenger/Help/ArticleItem'
import ArticleItems from '../../components/Messenger/Help/ArticleItems'
import Navigation from '../../components/Messenger/Navigation'
import BackButton from '../../components/Messenger/Navigation/BackButton'
import { useHistory, useParams } from 'react-router-dom'
import CloseButton from '../../components/Messenger/Navigation/CloseButton'
import { Author, HelpCenterArticleCollection, Locale } from '../../../types'
import { useTranslation } from 'react-i18next'
import { HelpCenterController } from '../../controllers'
import PageLoader from '../../../help_center/components/Loader/PageLoader'
import MobilityHelper from '../../../helpers/MobilityHelper'
import StackedAvatars from '../../../help_center/components/Avatar/StackedAvatars'
import CollectionItem from '../../components/Messenger/Help/CollectionItem'
import { AppState } from '../../store'
import { useSelector } from 'react-redux'
import Avatar from '../../components/Messenger/Avatar/Avatar'
import ScrollToTopOnMount from '../../effects/ScrollToTopOnMount'

const Header = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`

const Title = styled.div`
  font-weight: 600;
  line-height: 24px;
  color: rgb(26, 26, 26);
`

const Description = styled.div`
  font-size: 14px;
  line-height: 21px;
  margin-top: 4px;
  color: rgb(26, 26, 26);
  word-break: break-word;
`

const Metadata = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  line-height: 21px;
  color: rgb(115, 115, 115);
  margin-top: 12px;
`

const ArticleCount = styled.div``

const Collections = styled.div`
  display: flex;
  flex-direction: column;
`

interface IState {
  articleCollection: HelpCenterArticleCollection
  authors: Author[]
  articlesCount: number
  didInitialLoad: boolean
}

const HelpCollection = () => {
  const history = useHistory()
  const { id } = useParams<{ id: string }>()
  const { locale } = useSelector((state: AppState) => state.application)
  const { t } = useTranslation()
  const [state, setState] = React.useState<IState>({
    articleCollection: null,
    authors: [],
    articlesCount: 0,
    didInitialLoad: false,
  })

  const { didInitialLoad, articleCollection, authors, articlesCount } = state

  const fetchCollection = async () => {
    try {
      const { article_collection, authors, articles_count } = await HelpCenterController.getCollection(id)

      setState({
        articleCollection: article_collection,
        authors: authors,
        articlesCount: articles_count,
        didInitialLoad: true,
      })
    } catch (ex) {
      console.error(ex)
    }
  }

  React.useEffect(() => {
    fetchCollection().catch(console.error)
  }, [id])

  if (!didInitialLoad) {
    return <PageLoader />
  }

  const title = MobilityHelper.getTranslation(locale, articleCollection.translations, 'title')
  const description = MobilityHelper.getTranslation(locale, articleCollection.translations, 'description')
  const articles = articleCollection.articles || []
  const localeArticlesCount = articles.filter(article => Boolean(MobilityHelper.getTranslation(locale, article.translations, 'title'))).length
  const articleCollections = articleCollection.article_collections || []
  const localeCollectionsCount = articleCollections.filter(collection => Boolean(MobilityHelper.getTranslation(locale, collection.translations, 'title'))).length

  return (
    <>
      <Navigation
        left={<BackButton />}
        title={t('Help::Help')}
        right={<CloseButton />}
      />
      <ScrollToTopOnMount />

      <Header>
        <Title>{title}</Title>
        <Description>{description}</Description>
        <Metadata>
          <ArticleCount>
            {t('HelpCollection::{{count}} articles', { count: localeArticlesCount })}
          </ArticleCount>

          {authors.length > 0 && <StackedAvatars>
            {authors.slice(0, 3).map((author) => (
              <Avatar
                key={author.id}
                name={author.name}
                src={author.avatar_url}
                width={28}
                rounded
              />
            ))}
          </StackedAvatars>}
        </Metadata>
      </Header>

      {localeArticlesCount > 0 && <ArticleItems>
        {articles.map((article) => {
          return (
            <ArticleItem
              key={article.id}
              article={article}
            />
          )
        })}
      </ArticleItems>}


      {localeCollectionsCount > 0 && <Collections>
        {articleCollections.map((articleCollection) => {
          if (articleCollection.articles_count === 0) return null

          return (
            <CollectionItem
              key={articleCollection.id}
              collection={articleCollection}
            />
          )
        })}
      </Collections>}
    </>
  )
}

export default HelpCollection